<template>
  <!-- Data across activities (US 9370): from which business activities data flows into the system,
       and all the data the system holds -->
  <div v-if="data" class="sx-security df" qid="system-data-flow-tab">
    <div class="sx-kpis">
      <div class="sx-kpi">
        <small>{{ $t('data_flow.kpi.data') }}</small>
        <b>{{ data.counts.data }}</b>
        <span>{{ $tc('data_flow.kpi.sensitive', data.counts.sensitive, { count: data.counts.sensitive }) }}</span>
      </div>
      <div class="sx-kpi">
        <small>{{ $t('data_flow.kpi.activities') }}</small>
        <b>{{ data.counts.activities }}</b>
        <span>{{ $tc('data_flow.kpi.processes', data.counts.processes, { count: data.counts.processes }) }}</span>
      </div>
      <div class="sx-kpi" :class="{ 'is-warn': data.counts.not_on_system }">
        <small>{{ $t('data_flow.kpi.not_on_system') }}</small>
        <b>{{ data.counts.not_on_system }}</b>
        <span>{{ $t('data_flow.kpi.not_on_system_hint') }}</span>
        <button v-if="canEdit && data.counts.not_on_system" type="button" class="v2-link sx-kpi__edit" :disabled="saving" qid="data-flow-add-missing" @click="addMissing">
          <font-awesome-icon icon="plus"/>{{ $t('data_flow.add_all') }}
        </button>
      </div>
      <div class="sx-kpi">
        <small>{{ $t('data_flow.kpi.not_from_activities') }}</small>
        <b>{{ data.counts.not_from_activities }}</b>
        <span>{{ $t('data_flow.kpi.not_from_activities_hint') }}</span>
      </div>
    </div>

    <div class="sp-overview">
      <!-- Data flows in, one row per activity -->
      <section class="v2-card sp-sec">
        <div class="sp-sec__head">
          <font-awesome-icon icon="project-diagram"/>
          <h3>{{ $t('data_flow.flows_title') }}</h3>
          <span class="sp-sec__state">{{ $t('data_flow.flows_subtitle', { name: system.name }) }}</span>
        </div>
        <div v-if="selectedData" class="df-filter">
          {{ $t('data_flow.filtered', { name: selectedData.name }) }}
          <button type="button" class="v2-link" @click="selected = null">{{ $t('data_flow.clear') }}</button>
        </div>
        <div v-if="data.activities.length" class="df-flow df-flow--head">
          <span>{{ $t('data_flow.from_activity') }}</span>
          <span></span>
          <span>{{ $t('data_flow.data_into', { name: system.name }) }}</span>
        </div>
        <div v-for="activity in data.activities"
             :key="activity.link_id"
             class="df-flow"
             :class="{ 'is-dim': selected && !activity.data.includes(selected) }"
             :qid="'data-flow-' + activity.link_id">
          <div class="df-flow__from">
            <router-link v-if="activity.client === getClient.slug" :to="{ name: 'activities.view', params: { slug: activity.slug } }" class="df-flow__name">{{ activity.name }}</router-link>
            <span v-else class="df-flow__name">{{ activity.name }}</span>
            <small v-if="activity.processes.length">{{ processPath(activity.processes[0]) }}<template v-if="activity.processes.length > 1"> +{{ activity.processes.length - 1 }}</template></small>
            <span v-if="activity.role" class="sp-chip df-flow__role">{{ activity.role }}</span>
          </div>
          <svg class="df-flow__arrow" viewBox="0 0 40 12" aria-hidden="true"><path d="M0 6h34M30 1l6 5-6 5"/></svg>
          <div class="df-flow__data">
            <button v-for="id in activity.data"
                    :key="id"
                    type="button"
                    class="sp-chip"
                    :class="{ 'is-sens': byId[id].sensitive, 'is-on': selected === id }"
                    @click="select(id)">{{ byId[id].name }}</button>
            <span v-if="!activity.data.length" class="df-flow__none"><font-awesome-icon icon="exclamation-triangle"/>{{ $t('data_flow.no_data') }}</span>
            <small v-if="activity.description" class="df-flow__desc">{{ activity.description | truncate(160) }}</small>
          </div>
        </div>
        <p v-if="!data.activities.length" class="sx-empty">{{ $t('data_flow.no_activities') }}</p>
      </section>

      <!-- All the data held -->
      <section class="v2-card sp-side__card df-held">
        <h4><font-awesome-icon icon="database"/>{{ $t('data_flow.held_title') }}</h4>
        <small class="sp-sub">{{ $t('data_flow.held_hint') }}</small>
        <div v-for="object in sortedData"
             :key="object.id"
             class="df-held__row"
             :class="{ 'is-on': selected === object.id }"
             :qid="'data-held-' + object.id">
          <button type="button" class="df-held__name" @click="select(object.id)">
            <span>{{ object.name }}<b v-if="object.sensitive" class="df-held__sens">{{ $t('data_flow.sensitive') }}</b></span>
            <small>{{ sourceText(object) }}</small>
          </button>
          <button v-if="canEdit && !object.own_link_id" type="button" class="v2-link df-held__act" :title="$t('data_flow.add_to_system')" :disabled="saving" @click="addToSystem(object.id)">
            <font-awesome-icon icon="plus"/>
          </button>
          <button v-else-if="canEdit && object.own_link_id && !object.activities.length" type="button" class="v2-link df-held__act is-danger" :title="$t('data_flow.remove_from_system')" :disabled="saving" @click="removeFromSystem(object)">
            <font-awesome-icon icon="times"/>
          </button>
          <font-awesome-icon v-else-if="object.own_link_id" icon="check" class="df-held__ok" :title="$t('data_flow.on_system')"/>
        </div>
        <p v-if="!data.data.length" class="sp-sub">{{ $t('data_flow.no_data_held') }}</p>
        <div v-if="canEdit" class="df-held__add">
          <b-form-input v-model="keyword" type="search" size="sm" :placeholder="$t('data_flow.add_placeholder')" qid="data-held-search" @input="search"></b-form-input>
          <div v-if="keyword && options.length" class="sx-options">
            <button v-for="option in options" :key="option.id" type="button" class="sx-option" :disabled="saving" @click="addToSystem(option.id)">
              <font-awesome-icon icon="plus"/><span class="sx-option__grow">{{ option.name }}</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';

  export default {
    name: 'SystemDataFlowTab',
    props: {
      system: { type: Object, required: true }
    },
    data: function() {
      return {
        data: null,
        selected: null,
        keyword: '',
        options: [],
        saving: false,
        searchTimer: null
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      canEdit() {
        return this.isGranted([this.permissions.SYSTEM_EDIT, this.permissions.SYSTEM_EDIT_OWN], this.system) && (!this.getClient.parent || this.system.partition);
      },
      byId() {
        let map = {};
        this.data.data.forEach(object => { map[object.id] = object; });
        return map;
      },
      selectedData() {
        return this.selected ? this.byId[this.selected] : null;
      },
      // Sensitive first, then by how many activities send it
      sortedData() {
        return this.data.data.slice().sort((a, b) => (b.sensitive - a.sensitive) || (b.activities.length - a.activities.length) || a.name.localeCompare(b.name));
      }
    },
    methods: {
      load() {
        let self = this;
        piincHttp.get('systems/' + this.system.slug + '/data-flows', { params: { client: this.getClient.slug } }).then(function(response) {
          self.data = response.data;
        }, function() {});
      },
      changed() {
        this.load();
        this.$emit('updated');
      },
      select(id) {
        this.selected = this.selected === id ? null : id;
      },
      processPath(process) {
        return (process.group ? process.group + ' › ' : '') + process.name;
      },
      sourceText(object) {
        let count = object.activities.length;
        if (!count) {
          return this.$t('data_flow.only_on_system');
        }
        let text = this.$tc('data_flow.from_activities', count, { count: count });
        return object.own_link_id ? text : text + ' · ' + this.$t('data_flow.not_on_list');
      },
      search() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(this.loadOptions, 300);
      },
      loadOptions() {
        let self = this;
        if (!this.keyword) {
          this.options = [];
          return;
        }
        piincHttp.get('data-objects', { params: { client: this.system.client || this.getClient.slug, keyword: this.keyword, per_page: 10 } }).then(function(response) {
          let held = self.data.data.filter(object => object.own_link_id).map(object => object.id);
          self.options = (response.data.items || response.data).filter(option => !held.includes(option.id));
        }, function() {});
      },
      post(id) {
        return piincHttp.post('systems/' + this.system.slug + '/data-objects', { data_object_id: id, system: this.system.slug });
      },
      addToSystem(id) {
        let self = this;
        this.saving = true;
        this.post(id).then(function() {
          self.saving = false;
          self.keyword = '';
          self.options = [];
          self.$toastr('success', self.$t('systems.data_objects.create.success'));
          self.changed();
        }, function() {
          self.saving = false;
        });
      },
      // Puts every category that flows in from activities on the system's own list, one by one
      addMissing() {
        let self = this;
        let missing = this.data.data.filter(object => !object.own_link_id && object.activities.length).map(object => object.id);
        this.saving = true;
        missing.reduce((chain, id) => chain.then(() => this.post(id)), Promise.resolve()).then(function() {
          self.saving = false;
          self.$toastr('success', self.$tc('data_flow.added_all', missing.length, { count: missing.length }));
          self.changed();
        }, function() {
          self.saving = false;
          self.changed();
        });
      },
      removeFromSystem(object) {
        let self = this;
        this.saving = true;
        piincHttp.delete('systems/' + this.system.slug + '/data-objects/' + object.own_link_id).then(function() {
          self.saving = false;
          self.$toastr('success', self.$t('data_flow.removed'));
          self.changed();
        }, function() {
          self.saving = false;
        });
      }
    },
    created() {
      this.load();
    }
  }
</script>
